import { useState } from 'react';
import { Timer } from '@repo/libs/Timer';
import { systemTimer } from '@repo/libs/SystemTimer';
import { useTimeout } from '../../hooks/useTimeout/useTimeout';
import { ToastAction } from '../useToast/Toast';

/** The toast a controller is holding, plus the key that arms its wait. */
export type ControlledToast<TToast> = TToast & { key: number };

export type ToastController<TToast> = {
  toast: ControlledToast<TToast> | undefined;
  showToast: (params: TToast) => void;
  closeToast: () => void;
  /** Taps the toast's button: closes the toast, then runs its `onClick`. */
  runAction: () => void;
};

/**
 * A provider's one toast: replaced, not stacked, hidden after
 * `autoHideDuration`, and kept while that is `undefined`.
 *
 * Tapping its button closes it: "Open" on the office's banner has opened the
 * chat, and a banner left on top would cover it.
 *
 * `key` is the wait's `restartKey`, so a replacement of the same duration gets
 * its own full time.
 *
 * The `Timer` is injected so a spec (or a provider's spec, via its `timer`
 * prop) can pass `new FakeTimer()` and advance past a real duration.
 *
 * Generic over the toast because `ToastProvider` and `SiteToastProvider` draw
 * in different primitives and share only this.
 */
export function useToastController<
  TToast extends { autoHideDuration?: number; action?: ToastAction },
>({ timer = systemTimer }: { timer?: Timer } = {}): ToastController<TToast> {
  const [toast, setToast] = useState<ControlledToast<TToast> | undefined>(
    undefined,
  );

  const showToast = (params: TToast) => {
    setToast(function replaceWhateverIsUp(current) {
      return { ...params, key: (current?.key ?? 0) + 1 };
    });
  };

  const closeToast = () => {
    setToast(undefined);
  };

  const runAction = () => {
    const action = toast?.action;
    closeToast();
    action?.onClick();
  };

  useTimeout(
    {
      callback: closeToast,
      delayMs: toast?.autoHideDuration,
      restartKey: toast?.key,
    },
    timer,
  );

  return { toast, showToast, closeToast, runAction };
}
